import { ChangeDetectionStrategy, Component, ViewChild, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@shared/components/dialog/dialog.component';
import { SharedModule } from '@shared/shared.module';
import {
  StandardAccountDictionaryEntry,
  StandardAccountDictionaryExclusion,
} from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary.response';
import { UpdateStandardAccountDictionaryEntryRequest } from '@interfaces/accounting-data-standardization/requests/standard-account-dictionary.request';
import {
  linesToList,
  listToLines,
  statementTypeLabel,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';

const DIALOG_KEY = 'ads-dictionary-entry-edit';

/** Limite do motivo da edição (o mesmo `Reason` da versão). */
const REASON_MAX_LENGTH = 2000;

/**
 * Edição manual de um verbete (PBI 3423, DPA-DIC-DB). Listas de texto ("Entra", "Erros comuns", "Decisões da casa") são
 * um item por linha; "NÃO entra" é uma lista de pares item e destino. Salvar manda todos os campos (as listas substituem
 * as atuais) e o motivo, obrigatório; o backend cria uma versão nova ativa. `open()` devolve o corpo do PUT ou `null`.
 */
@Component({
  selector: 'app-dictionary-entry-edit-dialog',
  standalone: true,
  imports: [FormsModule, SharedModule],
  templateUrl: './dictionary-entry-edit-dialog.component.html',
  styleUrl: './dictionary-entry-edit-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryEntryEditDialogComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef?: DialogComponent;

  protected readonly dialogKey = DIALOG_KEY;
  protected readonly reasonMaxLength = REASON_MAX_LENGTH;

  protected readonly entry = signal<StandardAccountDictionaryEntry | null>(null);
  protected readonly definition = signal('');
  protected readonly includesText = signal('');
  protected readonly excludes = signal<StandardAccountDictionaryExclusion[]>([]);
  protected readonly pitfallsText = signal('');
  protected readonly decisionsText = signal('');
  protected readonly normativeBasis = signal('');
  protected readonly reason = signal('');

  protected readonly statementLabel = computed(() =>
    statementTypeLabel(this.entry()?.statementType)
  );

  private resolver: ((body: UpdateStandardAccountDictionaryEntryRequest | null) => void) | null =
    null;

  constructor(translateService: TranslateService) {
    super(translateService);
  }

  open(
    entry: StandardAccountDictionaryEntry
  ): Promise<UpdateStandardAccountDictionaryEntryRequest | null> {
    this.resolve(null);
    this.entry.set(entry);
    this.definition.set(entry.definition ?? '');
    this.includesText.set(listToLines(entry.includes));
    this.excludes.set(
      (entry.excludes ?? []).map(item => ({ item: item.item ?? '', goesTo: item.goesTo ?? '' }))
    );
    this.pitfallsText.set(listToLines(entry.pitfalls));
    this.decisionsText.set(listToLines(entry.decisions));
    this.normativeBasis.set(entry.normativeBasis ?? '');
    this.reason.set('');
    return new Promise(resolve => {
      this.resolver = resolve;
      void this.dialogRef
        ?.open('', '', '', 'Cancelar', '', DIALOG_KEY, false, false)
        .then(() => this.resolve(null));
    });
  }

  onDialogClosed(): void {
    this.resolve(null);
  }

  close(): void {
    this.dialogRef?.close();
  }

  protected addExclusion(): void {
    this.excludes.update(list => [...list, { item: '', goesTo: '' }]);
  }

  protected removeExclusion(index: number): void {
    this.excludes.update(list => list.filter((_, position) => position !== index));
  }

  protected updateExclusion(index: number, field: 'item' | 'goesTo', value: string | null): void {
    this.excludes.update(list =>
      list.map((current, position) =>
        position === index ? { ...current, [field]: value ?? '' } : current
      )
    );
  }

  isFormValid(): boolean {
    const reason = this.reason().trim();
    return (
      !!this.entry()?.standardAccountId && reason.length > 0 && reason.length <= REASON_MAX_LENGTH
    );
  }

  submitAction(): void {
    const entry = this.entry();
    if (!entry?.standardAccountId || !this.isFormValid()) return;

    this.resolve({
      statementType: entry.statementType,
      standardAccountId: entry.standardAccountId,
      definition: this.definition().trim() || null,
      includes: linesToList(this.includesText()),
      excludes: this.excludes()
        .map(item => ({ item: item.item.trim(), goesTo: item.goesTo.trim() }))
        .filter(item => item.item.length > 0),
      pitfalls: linesToList(this.pitfallsText()),
      decisions: linesToList(this.decisionsText()),
      normativeBasis: this.normativeBasis().trim() || null,
      reason: this.reason().trim(),
    });
    this.close();
  }

  private resolve(body: UpdateStandardAccountDictionaryEntryRequest | null): void {
    const resolver = this.resolver;
    this.resolver = null;
    resolver?.(body);
  }
}
